import { useMemo, useState } from 'react';
import { fmtDate } from '@/lib/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Info, Loader2, MailCheck, Plus, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  doa,
  type DoaRowRow,
  type DoaRowStatus,
  type DoaVersionStatus,
} from '@/api/client';
import { AddStaffRowDialog } from './AddStaffRowDialog';
import { useDoaSignatureSelfHeal } from '@/hooks/useDoaSignatureSelfHeal';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * Sprint-10 — Center pane for the DoA page.
 *
 * Renders the selected version's header (status + version label), the row
 * table (staff, role, dates, row-status), and the lifecycle controls:
 *   - draft        → "+ Add staff member" + per-row remove + "Initiate signatures"
 *   - in_review    → signature progress summary (e.g. "3/5 staff signed · PI pending")
 *   - active/superseded/archived → read-only table
 *
 * Pulls its own data via doa.getVersion(binderId, versionId). The parent
 * supplies just the IDs so navigation between versions stays cheap (a new
 * tanstack-query cache key per version).
 */

const VERSION_STATUS_BADGE: Record<DoaVersionStatus, string> = {
  draft: 'badge badge-neutral',
  in_review: 'badge badge-warning',
  active: 'badge badge-success',
  superseded: 'badge badge-info',
  archived: 'badge badge-neutral',
};

function formatVersionStatus(s: DoaVersionStatus): string {
  return s.replace(/_/g, ' ');
}

function rowStatusDisplay(
  rowStatus: DoaRowStatus,
  versionStatus: DoaVersionStatus,
  waivedReason?: 'removed' | 'inactive' | null,
): { label: string; cls: string } {
  if (rowStatus === 'revoked') return { label: 'Revoked', cls: 'badge badge-danger' };
  // Staff member had left when the log was generated: PI signs alone.
  if (waivedReason) {
    const who = waivedReason === 'removed' ? 'Staff removed' : 'Staff inactive';
    return rowStatus === 'signed'
      ? { label: `${who} · PI signed`, cls: 'badge badge-success' }
      : { label: `${who} · PI to sign`, cls: 'badge badge-warning' };
  }
  if (rowStatus === 'signed') return { label: 'Signed', cls: 'badge badge-success' };
  // pending_signature — split by whether initiation has happened
  if (versionStatus === 'draft') return { label: 'Not initiated', cls: 'badge badge-neutral' };
  return { label: 'Pending Signature', cls: 'badge badge-warning' };
}

function formatDate(iso: string | null): string {
  return iso ? fmtDate(iso) : '—';
}

export interface DoaVersionDetailProps {
  binderId: string;
  versionId: string;
}

export function DoaVersionDetail({ binderId, versionId }: DoaVersionDetailProps) {
  const qc = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [rowToRemove, setRowToRemove] = useState<DoaRowRow | null>(null);
  const [removeReason, setRemoveReason] = useState('');
  const [resendingRowId, setResendingRowId] = useState<string | null>(null);

  // Phase 2 Defect 2.6 — poll while the DoA is in_review so the row-level
  // "Pending Signature → Signed" transitions surface without a manual
  // refresh. Mirrors the eConsent Step 2 pattern (Step2InformedConsent).
  // Polling stops as soon as the version leaves in_review (draft is
  // pre-initiation; active/superseded/archived are terminal for this
  // signing cycle).
  const q = useQuery({
    queryKey: ['binder', binderId, 'doa', 'version', versionId],
    queryFn: () => doa.getVersion(binderId, versionId),
    enabled: !!binderId && !!versionId,
    refetchInterval: (query) => {
      const status = query.state.data?.version?.status;
      return status === 'in_review' ? 10_000 : false;
    },
    staleTime: 8_000,
  });

  // Self-heal against DocuSeal directly — the poll above only re-reads
  // Caspio, so if the webhook never landed (or silently failed), a
  // row/PI signature stays "Pending Signature" forever with nothing to
  // notice. See useDoaSignatureSelfHeal.ts.
  useDoaSignatureSelfHeal(binderId, versionId, q.data?.version?.status === 'in_review');

  // ── Row removal (draft-only) ────────────────────────────────────────
  const removeRow = useMutation({
    mutationFn: () => {
      if (!rowToRemove) throw new Error('No row selected');
      return doa.removeRow(binderId, versionId, rowToRemove.doa_row_id, {
        reason: removeReason.trim(),
      });
    },
    onSuccess: () => {
      toast.success(`Removed ${rowToRemove?.staff_full_name ?? 'staff member'}`);
      qc.invalidateQueries({
        queryKey: ['binder', binderId, 'doa', 'version', versionId],
      });
      setRowToRemove(null);
      setRemoveReason('');
    },
    onError: (err) => {
      toast.error(errorText(err, 'Failed to remove row'));
    },
  });

  // ── Resend signing emails to a pending_signature row ────────────────
  const resend = useMutation({
    mutationFn: (rowId: string) => doa.resendRowEmails(rowId),
    onMutate: (rowId) => setResendingRowId(rowId),
    onSuccess: (_data, rowId) => {
      const row = rows.find((r) => r.doa_row_id === rowId);
      toast.success(`Resent signing email to ${row?.staff_full_name ?? 'staff member'}`);
    },
    onError: (err) => {
      toast.error(errorText(err, 'Failed to resend email'));
    },
    onSettled: () => setResendingRowId(null),
  });

  // ── Initiate-signatures (draft-only, rows ≥ 1) ──────────────────────
  const initiate = useMutation({
    mutationFn: () => doa.initiate(binderId, versionId),
    onSuccess: (res) => {
      const n = res.envelopes_created;
      toast.success(
        `Envelopes sent to ${n} staff member${n === 1 ? '' : 's'} · PI countersign next`,
      );
      qc.invalidateQueries({
        queryKey: ['binder', binderId, 'doa', 'version', versionId],
      });
      qc.invalidateQueries({ queryKey: ['binder', binderId, 'doa', 'versions'] });
      qc.invalidateQueries({ queryKey: ['binder', binderId, 'doa', 'active'] });
    },
    onError: (err) => {
      toast.error(errorText(err, 'Failed to initiate signatures'));
    },
  });

  const rows: DoaRowRow[] = q.data?.rows ?? [];
  const version = q.data?.version;

  const signedCount = useMemo(() => rows.filter((r) => r.row_status === 'signed').length, [rows]);
  const totalCount = rows.length;
  // Sprint-18: BE field is `effective_at` (timestamp the version was
  // activated). Old code looked at `activated_at` which doesn't exist on
  // the BE row; this is the same notion: a version with a non-null
  // effective_at has been promoted past in_review.
  const piPending = version?.effective_at == null;

  if (q.isLoading) {
    return (
      <div className="card flex items-center gap-2 p-8 text-sm text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading DoA version…
      </div>
    );
  }

  if (q.isError || !version) {
    return (
      <div className={`flex items-start gap-2 rounded border p-4 text-sm ${errorToneClass(q.error)}`}>
        {isPermissionError(q.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
        <span>{errorText(q.error, 'Failed to load DoA version.')}</span>
      </div>
    );
  }

  const isDraft = version.status === 'draft';
  const isInReview = version.status === 'in_review';
  const canInitiate = isDraft && rows.length >= 1;

  return (
    <div className="card flex h-full min-h-0 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-surface-border bg-surface-muted/40 px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-ink-primary">
            {version.reference_number ?? `DoA v${version.version_number}`}
          </h2>
          <span className={`${VERSION_STATUS_BADGE[version.status]} capitalize text-[10px]`}>
            {formatVersionStatus(version.status)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isDraft ? (
            <>
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => setShowAddDialog(true)}
              >
                <Plus className="h-3.5 w-3.5" />
                Add staff member
              </button>
              <button
                type="button"
                className="btn-primary text-xs"
                disabled={!canInitiate || initiate.isPending}
                onClick={() => initiate.mutate()}
              >
                {initiate.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5" />
                )}
                Initiate signatures
              </button>
            </>
          ) : null}
          {isInReview ? (
            <span className="text-[11px] text-ink-muted">
              {signedCount}/{totalCount} staff signed
              {piPending ? ' · PI pending' : ' · PI signed'}
            </span>
          ) : null}
        </div>
      </div>

      {/* Row table */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {rows.length === 0 ? (
          <div className="flex h-full items-center justify-center p-8 text-sm text-ink-muted">
            {isDraft
              ? 'No staff added yet — use "+ Add staff member" to start.'
              : 'No rows on this version.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-surface-base text-[11px] uppercase tracking-wider text-ink-muted">
              <tr className="border-b border-surface-border">
                <th className="px-4 py-2 text-left font-medium">Staff</th>
                <th className="px-4 py-2 text-left font-medium">Role</th>
                <th className="px-4 py-2 text-left font-medium">Effective from</th>
                <th className="px-4 py-2 text-left font-medium">Effective to</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                {isDraft || isInReview ? <th className="w-10 px-2 py-2" /> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.doa_row_id}
                  className="border-b border-surface-border/60 last:border-b-0 hover:bg-surface-muted/40"
                >
                  <td className="px-4 py-2 text-ink-primary">{row.staff_full_name}</td>
                  <td className="px-4 py-2 text-ink-primary">{row.role}</td>
                  <td className="px-4 py-2 text-ink-muted tabular-nums">
                    {formatDate(row.effective_from)}
                  </td>
                  <td className="px-4 py-2 text-ink-muted tabular-nums">
                    {formatDate(row.effective_to)}
                  </td>
                  <td className="px-4 py-2">
                    {(() => {
                      const { label, cls } = rowStatusDisplay(row.row_status, version.status, row.staff_signature_waived_reason);
                      return <span className={`${cls} text-[10px]`}>{label}</span>;
                    })()}
                  </td>
                  {isDraft ? (
                    <td className="px-2 py-2 text-right">
                      {row.row_status !== 'revoked' && (
                        <button
                          type="button"
                          className="text-ink-muted hover:text-danger-700 disabled:cursor-not-allowed disabled:opacity-40"
                          title="Remove row"
                          aria-label={`Remove ${row.staff_full_name}`}
                          disabled={removeRow.isPending}
                          onClick={() => {
                            setRowToRemove(row);
                            setRemoveReason('');
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  ) : isInReview ? (
                    <td className="px-2 py-2 text-right">
                      {row.row_status === 'pending_signature' && !row.staff_signature_waived_reason && (
                        <button
                          type="button"
                          className="text-ink-muted hover:text-accent-600 disabled:cursor-not-allowed disabled:opacity-40"
                          title="Resend signing email"
                          aria-label={`Resend signing email to ${row.staff_full_name}`}
                          disabled={resend.isPending}
                          onClick={() => resend.mutate(row.doa_row_id)}
                        >
                          {resendingRowId === row.doa_row_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <MailCheck className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AddStaffRowDialog
        binderId={binderId}
        versionId={versionId}
        existingUserIds={rows.map((r) => r.staff_user_id)}
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onAdded={() => {
          /* invalidation handled inside the dialog */
        }}
      />

      {/* Remove-row confirmation dialog */}
      {rowToRemove && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center !m-0 bg-black/60 backdrop-blur-sm p-4 !m-0"
          onClick={(e) => {
            if (e.target === e.currentTarget && !removeRow.isPending) {
              setRowToRemove(null);
              setRemoveReason('');
            }
          }}
        >
          <div className="card w-full max-w-sm space-y-4 p-6">
            <div className="flex items-start gap-3">
              <Trash2 className="mt-0.5 h-4 w-4 shrink-0 text-danger-600" />
              <div>
                <h2 className="text-base font-semibold text-ink-primary">
                  Remove {rowToRemove.staff_full_name}?
                </h2>
                <p className="mt-0.5 text-xs text-ink-muted">
                  This removes them from DoA version {version?.reference_number}. Please provide a
                  brief reason.
                </p>
              </div>
            </div>
            <div>
              <label className="label text-xs" htmlFor="remove-reason">
                Reason *
              </label>
              <textarea
                id="remove-reason"
                className="input min-h-[72px] w-full"
                placeholder="e.g. Staff no longer part of study team"
                maxLength={500}
                value={removeReason}
                disabled={removeRow.isPending}
                onChange={(e) => setRemoveReason(e.target.value)}
              />
              {removeReason.trim().length > 0 && removeReason.trim().length < 4 && (
                <p className="mt-1 text-[11px] text-danger-700">
                  Reason must be at least 4 characters.
                </p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className="btn-ghost"
                disabled={removeRow.isPending}
                onClick={() => {
                  setRowToRemove(null);
                  setRemoveReason('');
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn bg-danger-500 text-white shadow-card hover:bg-danger-700 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={removeReason.trim().length < 4 || removeRow.isPending}
                onClick={() => removeRow.mutate()}
              >
                {removeRow.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
